
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { CurveState } from "../../types/bonding-curve";
import { BONDING_CURVE_TARGET_SOL, BONDING_CURVE_TARGET_SUI } from "../constants";

// --- Constants ---
const BASE_FEE_BPS = 25; // 0.25%
const GRADUATION_THRESHOLD_PERCENT = 99.5; // Trigger at 99.5% to avoid rounding errors
const INITIAL_MAX_BUY_PERCENT = 5; // Max 5% of supply per tx initially (Bot Protection)
const SNIPE_COOLDOWN_MS = 2000; // 2 seconds cooldown per wallet

/**
 * Calculates the amount of tokens received for a given input of collateral (SOL/SUI).
 * Uses Constant Product Formula: x * y = k
 */
export const calculateBuyOutput = (
    state: CurveState, 
    amountIn: number
): { amountOut: number, fee: number, priceImpact: number } => {
    const fee = amountIn * (BASE_FEE_BPS / 10000);
    const netIn = amountIn - fee;

    const x = state.virtualSolReserves;
    const y = state.virtualTokenReserves;
    const k = x * y;

    // New Virtual SOL Reserve = Old + Net Input
    const newX = x + netIn;
    
    // New Virtual Token Reserve = k / New Virtual SOL
    const newY = k / newX;
    
    // Tokens Out = Old Tokens - New Tokens
    const amountOut = y - newY;

    // Price Impact Calculation
    const oldPrice = x / y;
    const newPrice = newX / newY;
    const priceImpact = Math.abs((newPrice - oldPrice) / oldPrice) * 100;

    return { amountOut, fee, priceImpact };
};

/**
 * Calculates the amount of collateral (SOL/SUI) received for selling tokens.
 */
export const calculateSellOutput = (
    state: CurveState,
    amountIn: number
): { amountOut: number, fee: number, priceImpact: number } => {
    const x = state.virtualSolReserves;
    const y = state.virtualTokenReserves;
    const k = x * y;

    // New Virtual Token Reserve = Old + Input Tokens
    const newY = y + amountIn;
    
    // New Virtual SOL Reserve = k / New Virtual Tokens
    const newX = k / newY;
    
    // SOL Out = Old SOL - New SOL
    const grossOut = x - newX;

    const fee = grossOut * (BASE_FEE_BPS / 10000);
    const amountOut = grossOut - fee;

    // Price Impact
    const oldPrice = x / y;
    const newPrice = newX / newY;
    const priceImpact = Math.abs((oldPrice - newPrice) / oldPrice) * 100;

    return { amountOut, fee, priceImpact };
};

/**
 * Checks if the curve has reached its funding target for graduation.
 */
export const checkGraduationThreshold = (state: CurveState): boolean => {
    const target = state.chain === 'SOL' ? BONDING_CURVE_TARGET_SOL : BONDING_CURVE_TARGET_SUI;
    // We subtract the initial virtual reserves to get the "Real" collected amount
    const collected = state.realSolReserves; 
    
    const progress = (collected / target) * 100;
    return progress >= GRADUATION_THRESHOLD_PERCENT;
};

/**
 * Bot Protection Logic
 * 1. Checks if transaction exceeds max buy limit (Anti-Whale/Snipe).
 * 2. Checks for rapid-fire transactions from same source (not implemented here without tx history, but placeholder logic exists).
 */
export const validateBotProtection = (
    state: CurveState, 
    amountIn: number, 
    side: 'BUY' | 'SELL',
    userAddress: string
): { allowed: boolean; reason?: string } => {
    
    // If graduated, protections are lifted
    if (state.isGraduated) return { allowed: true };

    // 1. Max Transaction Limit check (Buy Side)
    if (side === 'BUY' && state.security) {
        const currentSupply = state.virtualTokenReserves; // Approximate available
        // Estimate tokens out simply for check
        const estimatedOut = (amountIn / state.tokenPrice); 
        
        // Dynamic max buy based on curve progress? 
        // For now, fixed percentage of initial supply if defined
        if (estimatedOut > state.security.maxTradeAmount) {
            return { 
                allowed: false, 
                reason: `Anti-Snipe: Buy amount exceeds limit of ${state.security.maxTradeAmount.toLocaleString()} tokens.` 
            };
        }
    }

    // 2. Launch Cooldown (Prevent buying in same block/second as deploy)
    // Simulated check
    const timeSinceLaunch = Date.now() - (state.security?.launchTimestamp || 0);
    if (timeframeProtection(timeSinceLaunch)) {
         // Allow, effectively just a very short buffer
    }

    return { allowed: true };
};

const timeframeProtection = (deltaMs: number) => {
    // Prevent trades in the first 500ms (Flashbot mitigation simulation)
    return deltaMs > 500;
}
